import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash-es';
import classNames from 'classnames';
import { MapRef } from 'react-map-gl';
import { Edit, Locked, Close, Copy, Add } from '@carbon/react/icons';
import { Button, Checkbox, RadioButtonGroup, RadioButton } from '@carbon/react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { activeTcWorkspaceAtom } from 'components/type-curve-workspace/atoms';
import { TcAreaPickerButton } from 'components/type-curve-workspace/tc-area-picker-button';
import { useWellFilterSync } from './use-saved-well-filters';
import {
  groupAppliedFilters,
  formatRangeValue,
  resolveFacetName,
} from './filter-labels';
import {
  pageSectionVisibilityAtom,
  effectivePageSectionVisibilityAtom,
  tcHasTypeCurveAreaAtom,
  EMPTY_STATE_HIDDEN_SECTIONS,
  tcMapFilterModeAtom,
  tcSpatialFilterDisabledAtom,
  tcMapRefAtom,
  tcAoiPolygonAtom,
  tcAoiPolygonLockedAtom,
  tcMapBoundsAtom,
  probitPlotChartAtom,
  monthlyVolumesChartAtom,
  ratiosChartAtom,
  tcWellFilterModalOpenAtom,
  tcWellFilterModalCategoryAtom,
  tcWellFiltersAtom,
  tcPendingWellFiltersAtom,
  tcDisabledWellFilterCategoriesAtom,
  tcColorByModalOpenAtom,
  tcColorByRulesAtom,
  tcWellsFacetsAtom,
  tcCrossPlotChartsAtom,
  tcCrossPlotChartModalOpenAtom,
  tcEditingCrossPlotChartIdAtom,
  tcUnitFilterModalOpenAtom,
  tcUnitFilterModalCategoryAtom,
  tcUnitFiltersAtom,
  tcDisabledUnitFilterCategoriesAtom,
  tcPendingUnitFiltersAtom,
  tcUnitsFacetsAtom,
  tcUnitColorByModalOpenAtom,
  tcUnitColorByRulesAtom,
} from './atoms';
import {
  CrossPlotChartConfig,
  TcMapFilterMode,
  PAGE_SECTIONS,
  PageSection,
  PageSectionKey,
  ColorByTarget,
  ColorByConfig,
  TcUnitFilters,
  TcUnitsFacets,
} from '../types';
import {
  ColorSwatch,
  hexToRgba,
  DEFAULT_COLORS,
} from 'components/color-picker';
import { format0 } from 'utils/number';
import styles from './type-curve-page-filters.module.scss';
import { TC_WELL_FILTER_CATEGORIES, TC_UNIT_FILTER_CATEGORIES } from './utils';

const GlobalMapFilter = () => {
  const [filterMode, setFilterMode] = useAtom(tcMapFilterModeAtom);
  const [spatialDisabled, setSpatialDisabled] = useAtom(
    tcSpatialFilterDisabledAtom
  );
  const mapRef = useAtomValue(tcMapRefAtom);
  const [aoiPolygon, setAoiPolygon] = useAtom(tcAoiPolygonAtom);
  const [aoiLocked, setAoiLocked] = useAtom(tcAoiPolygonLockedAtom);
  const setMapBounds = useSetAtom(tcMapBoundsAtom);
  const moveendListenerRef = useRef<(() => void) | null>(null);

  const updateBoundsFromMap = useMemo(
    () =>
      debounce((map: MapRef) => {
        const bounds = map.getBounds();
        if (!bounds) return;
        setMapBounds({
          sw_lng: bounds.getWest(),
          sw_lat: bounds.getSouth(),
          ne_lng: bounds.getEast(),
          ne_lat: bounds.getNorth(),
        });
      }, 500),
    [setMapBounds]
  );

  useEffect(() => {
    if (filterMode === 'visible-extent' && mapRef) {
      const handler = () => updateBoundsFromMap(mapRef);
      mapRef.on('moveend', handler);
      moveendListenerRef.current = handler;
      handler();
      return () => {
        mapRef.off('moveend', handler);
        moveendListenerRef.current = null;
      };
    }

    if (moveendListenerRef.current && mapRef) {
      mapRef.off('moveend', moveendListenerRef.current);
      moveendListenerRef.current = null;
    }
    return undefined;
  }, [filterMode, mapRef, updateBoundsFromMap]);

  const handleChange = useCallback(
    (value: string | number | undefined) => {
      const mode = value as TcMapFilterMode;
      setFilterMode(mode);

      if (mode === 'type-curve-area') {
        setAoiPolygon(null);
        setAoiLocked(true);
        setMapBounds(null);
      }
    },
    [setFilterMode, setAoiPolygon, setAoiLocked, setMapBounds]
  );

  const handleEditPolygon = useCallback(() => {
    setAoiLocked(false);
  }, [setAoiLocked]);

  const handleLockPolygon = useCallback(() => {
    setAoiLocked(true);
  }, [setAoiLocked]);

  const handleDeletePolygon = useCallback(() => {
    setAoiPolygon(null);
    setAoiLocked(true);
  }, [setAoiPolygon, setAoiLocked]);

  return (
    <div className={styles.mapFilterControls}>
      <RadioButtonGroup
        legendText=""
        name="tc-global-map-filter"
        valueSelected={filterMode}
        onChange={handleChange}
        orientation="vertical"
      >
        <RadioButton
          id="tc-map-filter-area"
          labelText="Type Curve Area"
          value="type-curve-area"
        />
        <RadioButton
          id="tc-map-filter-extent"
          labelText="Use Visible Map Extent"
          value="visible-extent"
        />
        <RadioButton
          id="tc-map-filter-polygon"
          labelText="Draw AOI Polygon"
          value="aoi-polygon"
        />
      </RadioButtonGroup>
      {filterMode === 'aoi-polygon' && aoiPolygon && (
        <div
          className={classNames(styles.polygonGroup, {
            [styles.mapFilterDisabled]: spatialDisabled,
          })}
        >
          <div className={styles.polygonHeader}>
            <span className={styles.polygonName}>AOI Polygon</span>
            {aoiLocked ? (
              <>
                <Button
                  kind="ghost"
                  size="sm"
                  className={styles.appliedFilterToggle}
                  onClick={() => setSpatialDisabled((d) => !d)}
                >
                  {spatialDisabled ? 'Enable' : 'Disable'}
                </Button>
                <button
                  type="button"
                  aria-label="Edit polygon"
                  onClick={handleEditPolygon}
                  className={classNames(
                    styles.polygonAction,
                    styles.polygonActionEdit
                  )}
                >
                  <Edit />
                </button>
                <button
                  type="button"
                  aria-label="Delete polygon"
                  onClick={handleDeletePolygon}
                  className={styles.polygonAction}
                >
                  <Close />
                </button>
              </>
            ) : (
              <Button
                kind="primary"
                size="sm"
                renderIcon={Locked}
                onClick={handleLockPolygon}
              >
                Done
              </Button>
            )}
          </div>
          <div className={styles.chipList}>
            {aoiLocked ? (
              <span className={styles.polygonChip}>Polygon drawn</span>
            ) : (
              <span className={styles.editingHint}>
                Edit vertices on the map, then click Done
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ProbitPlotChart = () => {
  const [vis, setVis] = useAtom(probitPlotChartAtom);
  const keys = ['oil', 'gas', 'water'] as const;
  const labels = { oil: 'Oil', gas: 'Gas', water: 'Water' };
  const allVisible = keys.every((k) => vis[k]);
  const someVisible = keys.some((k) => vis[k]);

  return (
    <div className={styles.chartFilter}>
      <Checkbox
        id="tc-probit-all"
        labelText="Hide All"
        checked={!someVisible}
        indeterminate={someVisible && !allVisible}
        onChange={() => {
          const v = !allVisible;
          setVis({ oil: v, gas: v, water: v });
        }}
      />
      {keys.map((k) => (
        <Checkbox
          key={k}
          id={`tc-probit-${k}`}
          labelText={labels[k]}
          checked={vis[k]}
          onChange={(_evt, { checked }) =>
            setVis((prev) => ({ ...prev, [k]: checked }))
          }
        />
      ))}
    </div>
  );
};

const MonthlyVolumesChart = () => {
  const [vis, setVis] = useAtom(monthlyVolumesChartAtom);
  const keys = ['oil', 'gas', 'water'] as const;
  const labels = { oil: 'Oil', gas: 'Gas', water: 'Water' };
  const allVisible = keys.every((k) => vis[k]);
  const someVisible = keys.some((k) => vis[k]);

  return (
    <div className={styles.chartFilter}>
      <Checkbox
        id="tc-monthly-all"
        labelText="Hide All"
        checked={!someVisible}
        indeterminate={someVisible && !allVisible}
        onChange={() => {
          const v = !allVisible;
          setVis({ oil: v, gas: v, water: v });
        }}
      />
      {keys.map((k) => (
        <Checkbox
          key={k}
          id={`tc-monthly-${k}`}
          labelText={labels[k]}
          checked={vis[k]}
          onChange={(_evt, { checked }) =>
            setVis((prev) => ({ ...prev, [k]: checked }))
          }
        />
      ))}
    </div>
  );
};

const RatiosChart = () => {
  const [vis, setVis] = useAtom(ratiosChartAtom);
  const keys = ['gor', 'wor', 'wgr', 'cgr', 'watercut'] as const;
  const labels = {
    gor: 'GOR',
    wor: 'WOR',
    wgr: 'WGR',
    cgr: 'CGR',
    watercut: 'Watercut',
  };
  const allVisible = keys.every((k) => vis[k]);
  const someVisible = keys.some((k) => vis[k]);

  return (
    <div className={styles.chartFilter}>
      <Checkbox
        id="tc-ratios-all"
        labelText="Hide All"
        checked={!someVisible}
        indeterminate={someVisible && !allVisible}
        onChange={() => {
          const v = !allVisible;
          setVis({ gor: v, wor: v, wgr: v, cgr: v, watercut: v });
        }}
      />
      {keys.map((k) => (
        <Checkbox
          key={k}
          id={`tc-ratios-${k}`}
          labelText={labels[k]}
          checked={vis[k]}
          onChange={(_evt, { checked }) =>
            setVis((prev) => ({ ...prev, [k]: checked }))
          }
        />
      ))}
    </div>
  );
};

const CrossPlotChart = () => {
  const [charts, setCharts] = useAtom(tcCrossPlotChartsAtom);
  const setModalOpen = useSetAtom(tcCrossPlotChartModalOpenAtom);
  const setEditingId = useSetAtom(tcEditingCrossPlotChartIdAtom);

  const allVisible = charts.length > 0 && charts.every((c) => c.visible);
  const someVisible = charts.some((c) => c.visible);

  const handleAddChart = useCallback(() => {
    setEditingId(null);
    setModalOpen(true);
  }, [setEditingId, setModalOpen]);

  const handleToggleAll = useCallback(() => {
    const next = !allVisible;
    setCharts((prev) => prev.map((c) => ({ ...c, visible: next })));
  }, [allVisible, setCharts]);

  const handleToggleChart = useCallback(
    (id: string, visible: boolean) => {
      setCharts((prev) =>
        prev.map((c) => (c.id === id ? { ...c, visible } : c))
      );
    },
    [setCharts]
  );

  const handleEdit = useCallback(
    (chart: CrossPlotChartConfig) => {
      setEditingId(chart.id);
      setModalOpen(true);
    },
    [setEditingId, setModalOpen]
  );

  const handleDuplicate = useCallback(
    (chart: CrossPlotChartConfig) => {
      const copy: CrossPlotChartConfig = {
        ...chart,
        id: `cross-plot-${Date.now()}-${Math.round(Math.random() * 1_000_000)}`,
        title: `${chart.title} (copy)`,
        visible: true,
      };
      setCharts((prev) => [...prev, copy]);
    },
    [setCharts]
  );

  const handleRemove = useCallback(
    (id: string) => {
      setCharts((prev) => prev.filter((c) => c.id !== id));
    },
    [setCharts]
  );

  return (
    <div className={styles.chartFilter}>
      <div className={styles.wellsFilterActions}>
        <Button
          kind="tertiary"
          size="sm"
          onClick={handleAddChart}
          renderIcon={Add}
        >
          Add Chart
        </Button>
      </div>

      {charts.length > 0 && (
        <Checkbox
          id="tc-crossplot-show-all"
          labelText="Show All"
          checked={allVisible}
          indeterminate={!allVisible && someVisible}
          onChange={handleToggleAll}
        />
      )}

      {charts.map((chart) => (
        <div key={chart.id} className={styles.crossPlotChartRow}>
          <Checkbox
            id={`tc-crossplot-${chart.id}`}
            labelText={chart.title}
            checked={chart.visible}
            onChange={(_evt, { checked }) =>
              handleToggleChart(chart.id, checked)
            }
          />
          <div className={styles.crossPlotChartActions}>
            <button
              type="button"
              className={classNames(
                styles.polygonAction,
                styles.polygonActionEdit
              )}
              onClick={() => handleEdit(chart)}
              aria-label={`Edit ${chart.title}`}
            >
              <Edit />
            </button>
            <button
              type="button"
              className={classNames(
                styles.polygonAction,
                styles.polygonActionEdit
              )}
              onClick={() => handleDuplicate(chart)}
              aria-label={`Duplicate ${chart.title}`}
            >
              <Copy />
            </button>
            <button
              type="button"
              className={styles.polygonAction}
              onClick={() => handleRemove(chart.id)}
              aria-label={`Remove ${chart.title}`}
            >
              <Close />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const PageSettings = ({ sections }: { sections: readonly PageSection[] }) => {
  const [visibility, setVisibility] = useAtom(pageSectionVisibilityAtom);
  // Effective visibility drives the displayed checked state so the 5 gated
  // sections read as unchecked in the empty state; stored intent is preserved
  // for auto-restore once an area appears.
  const effectiveVisibility = useAtomValue(effectivePageSectionVisibilityAtom);
  const isEmptyState = useAtomValue(tcHasTypeCurveAreaAtom) === false;

  const allVisible = sections.every(({ key }) => visibility[key]);
  const someVisible = sections.some(({ key }) => visibility[key]);

  const handleToggleAll = useCallback(() => {
    const newState = !allVisible;
    // Merge instead of replace: only the rendered sections change, so the
    // intent for sections this page doesn't offer stays untouched.
    setVisibility((prev) => ({
      ...prev,
      ...(Object.fromEntries(
        sections.map(({ key }) => [key, newState])
      ) as Partial<Record<PageSectionKey, boolean>>),
    }));
  }, [allVisible, sections, setVisibility]);

  const handleToggle = useCallback(
    (key: PageSectionKey, checked: boolean) => {
      setVisibility((prev) => ({ ...prev, [key]: checked }));
    },
    [setVisibility]
  );

  return (
    <div className={styles.pageSettings}>
      <Checkbox
        id="tc-page-settings-all"
        labelText="Hide all"
        checked={!someVisible}
        indeterminate={!allVisible && !someVisible === false && someVisible}
        onChange={() => handleToggleAll()}
      />
      {sections.map(({ key, label }) => {
        const gated = isEmptyState && EMPTY_STATE_HIDDEN_SECTIONS.includes(key);
        return (
          <Checkbox
            key={key}
            id={`tc-page-settings-${key}`}
            labelText={label}
            checked={effectiveVisibility[key]}
            disabled={gated}
            onChange={(_evt, { checked }) => handleToggle(key, checked)}
          />
        );
      })}
    </div>
  );
};

const AppliedFilterCard = ({
  label,
  values,
  onEdit,
  onRemove,
  disableEdit,
  enabled,
  onToggleEnabled,
}: {
  label: string;
  values: string[];
  onEdit: () => void;
  onRemove: () => void;
  disableEdit?: boolean;
  // Omitted for filters that can't be toggled (no persisted row to flip).
  enabled?: boolean;
  onToggleEnabled?: () => void;
}) => (
  <div
    className={classNames(styles.appliedFilter, {
      [styles.appliedFilterDisabled]: enabled === false,
    })}
  >
    <div className={styles.appliedFilterHeader}>
      <span className={styles.appliedFilterName}>{label}</span>
      <div className={styles.appliedFilterActions}>
        {onToggleEnabled ? (
          <Button
            kind="ghost"
            size="sm"
            className={styles.appliedFilterToggle}
            onClick={onToggleEnabled}
          >
            {enabled === false ? 'Enable' : 'Disable'}
          </Button>
        ) : null}
        <button
          type="button"
          className={classNames(styles.polygonAction, styles.polygonActionEdit)}
          onClick={onEdit}
          aria-label={`Edit ${label}`}
          disabled={disableEdit}
        >
          <Edit />
        </button>
        <button
          type="button"
          className={styles.polygonAction}
          onClick={onRemove}
          aria-label={`Remove ${label}`}
        >
          <Close />
        </button>
      </div>
    </div>
    <div className={styles.appliedFilterChips}>
      {values.map((v) => (
        <span key={v} className={styles.appliedFilterChip}>
          {v}
        </span>
      ))}
    </div>
  </div>
);

const AppliedFilters = () => {
  const [filters, setFilters] = useAtom(tcWellFiltersAtom);
  const setPending = useSetAtom(tcPendingWellFiltersAtom);
  const setModalOpen = useSetAtom(tcWellFilterModalOpenAtom);
  const setModalCategory = useSetAtom(tcWellFilterModalCategoryAtom);
  const facets = useAtomValue(tcWellsFacetsAtom);
  const [disabledCategories, setDisabledCategories] = useAtom(
    tcDisabledWellFilterCategoriesAtom
  );
  const { removeCategory } = useWellFilterSync();

  const groups = useMemo(
    () => groupAppliedFilters(filters, facets),
    [filters, facets]
  );

  if (groups.length === 0) return null;

  // A numeric range groups under "Label (from)" / "Label (to)", so match the
  // base label too.
  const categoryForLabel = (label: string) => {
    const baseLabel = label.replace(/ \(from\)$| \(to\)$/, '');
    return TC_WELL_FILTER_CATEGORIES.find(
      (c) => c.label === baseLabel || c.label === label
    );
  };

  // Open the modal on the card's own category.
  const handleEdit = (label: string) => {
    setModalCategory(categoryForLabel(label)?.id ?? null);
    setPending({ ...filters });
    setModalOpen(true);
  };

  const handleToggle = (label: string) => {
    const cat = categoryForLabel(label);
    if (!cat) return;
    setDisabledCategories((prev) => {
      return prev.includes(cat.id)
        ? prev.filter((id) => id !== cat.id)
        : [...prev, cat.id];
    });
  };

  const isEnabled = (label: string) => {
    const cat = categoryForLabel(label);
    if (!cat) return true;
    return !disabledCategories.includes(cat.id);
  };

  // Remove a category's persisted filter row (DELETE), then clear its flat keys
  // so the analysis refetches without it.
  const handleRemove = async (label: string) => {
    const cat = categoryForLabel(label);
    if (!cat) return;

    await removeCategory(cat.id);

    const updated = { ...filters };
    if (cat.type === 'picklist') {
      updated[cat.param] = [];
    } else {
      delete updated[cat.param];
    }
    if (cat.minParam) delete updated[cat.minParam];
    if (cat.maxParam) delete updated[cat.maxParam];
    setFilters(updated);
  };

  return (
    <div className={styles.appliedFiltersContainer}>
      {groups.map((group) => (
        <AppliedFilterCard
          key={group.label}
          label={group.label}
          values={group.values}
          onEdit={() => handleEdit(group.label)}
          onRemove={() => void handleRemove(group.label)}
          disableEdit={!facets}
          enabled={isEnabled(group.label)}
          onToggleEnabled={() => handleToggle(group.label)}
        />
      ))}
    </div>
  );
};

// ─── Unit Filter helpers + AppliedUnitFilters ───
// Mirror of the Wells helpers above, adapted to TcUnitsFacets (which uses
// `Array<{value, name?, count}>` for categorical facets instead of `buckets`).

const findUnitCategoryLabel = (paramKey: string): string => {
  for (const cat of TC_UNIT_FILTER_CATEGORIES) {
    if (cat.param === paramKey) return cat.label;
    if (cat.minParam === paramKey) return `${cat.label} (from)`;
    if (cat.maxParam === paramKey) return `${cat.label} (to)`;
  }
  return paramKey;
};

const resolveUnitFacetName = (
  value: string,
  paramKey: string,
  facets: TcUnitsFacets | null
): string => {
  if (!facets) return value;
  const cat = TC_UNIT_FILTER_CATEGORIES.find(
    (c) =>
      c.param === paramKey || c.minParam === paramKey || c.maxParam === paramKey
  );
  if (!cat?.facetKey) return value;
  const facet = facets[cat.facetKey as keyof TcUnitsFacets];
  if (!facet || !('buckets' in facet)) return value;
  const bucket = facet.buckets.find((b) => String(b.id) === value);
  return bucket?.name ?? value;
};

const getUnitFacetRange = (
  category: (typeof TC_UNIT_FILTER_CATEGORIES)[number],
  facets: TcUnitsFacets | null
): { min: number | string | null; max: number | string | null } | null => {
  if (!facets || !category.facetKey) return null;
  const facet = facets[category.facetKey as keyof TcUnitsFacets];
  if (!facet || 'buckets' in facet) return null;
  return { min: facet.min ?? null, max: facet.max ?? null };
};

const groupUnitAppliedFilters = (
  filters: TcUnitFilters,
  facets: TcUnitsFacets | null
) => {
  const groups: Record<string, { label: string; values: string[] }> = {};
  const processedRangeCategories = new Set<string>();

  for (const [key, value] of Object.entries(filters)) {
    if (
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    )
      continue;

    const rangeCategory = TC_UNIT_FILTER_CATEGORIES.find(
      (c) =>
        c.type === 'numeric-range' && (c.minParam === key || c.maxParam === key)
    );

    if (rangeCategory) {
      if (processedRangeCategories.has(rangeCategory.id)) continue;
      processedRangeCategories.add(rangeCategory.id);

      const minVal = filters[rangeCategory.minParam!] as
        number | string | undefined;
      const maxVal = filters[rangeCategory.maxParam!] as
        number | string | undefined;
      const facetRange = getUnitFacetRange(rangeCategory, facets);

      const displayMin =
        minVal !== undefined
          ? formatRangeValue(minVal, true)
          : formatRangeValue(facetRange?.min, true);
      const displayMax =
        maxVal !== undefined
          ? formatRangeValue(maxVal, true)
          : formatRangeValue(facetRange?.max, true);

      groups[rangeCategory.label] = {
        label: rangeCategory.label,
        values: [`${displayMin} - ${displayMax}`],
      };
      continue;
    }

    const label = findUnitCategoryLabel(key);
    if (Array.isArray(value)) {
      groups[label] = {
        label,
        values: value.map((v) => resolveUnitFacetName(String(v), key, facets)),
      };
    } else {
      groups[label] = { label, values: [String(value)] };
    }
  }

  return Object.values(groups);
};

const AppliedUnitFilters = () => {
  const [filters, setFilters] = useAtom(tcUnitFiltersAtom);
  const setPending = useSetAtom(tcPendingUnitFiltersAtom);
  const setModalOpen = useSetAtom(tcUnitFilterModalOpenAtom);
  const setModalCategory = useSetAtom(tcUnitFilterModalCategoryAtom);
  const facets = useAtomValue(tcUnitsFacetsAtom);
  const [disabledCategories, setDisabledCategories] = useAtom(
    tcDisabledUnitFilterCategoriesAtom
  );

  const groups = useMemo(
    () => groupUnitAppliedFilters(filters, facets),
    [filters, facets]
  );

  if (groups.length === 0) return null;

  const categoryForLabel = (label: string) => {
    const baseLabel = label.replace(/ \(from\)$| \(to\)$/, '');
    return TC_UNIT_FILTER_CATEGORIES.find(
      (c) => c.label === baseLabel || c.label === label
    );
  };

  const handleEdit = (label: string) => {
    setModalCategory(categoryForLabel(label)?.id ?? null);
    setPending({ ...filters });
    setModalOpen(true);
  };

  const handleToggle = (label: string) => {
    const cat = categoryForLabel(label);
    if (!cat) return;
    setDisabledCategories((prev) => {
      return prev.includes(cat.id)
        ? prev.filter((id) => id !== cat.id)
        : [...prev, cat.id];
    });
  };

  const isEnabled = (label: string) => {
    const cat = categoryForLabel(label);
    if (!cat) return true;
    return !disabledCategories.includes(cat.id);
  };

  const handleRemove = (label: string) => {
    const updated = { ...filters };
    const baseLabel = label.replace(/ \(from\)$| \(to\)$/, '');
    for (const cat of TC_UNIT_FILTER_CATEGORIES) {
      if (cat.label === baseLabel || cat.label === label) {
        if (cat.type === 'picklist') {
          updated[cat.param] = [];
        } else {
          delete updated[cat.param];
        }
        if (cat.minParam) delete updated[cat.minParam];
        if (cat.maxParam) delete updated[cat.maxParam];
      }
    }
    setFilters(updated);
  };

  return (
    <div className={styles.appliedFiltersContainer}>
      {groups.map((group) => (
        <AppliedFilterCard
          key={group.label}
          label={group.label}
          values={group.values}
          onEdit={() => handleEdit(group.label)}
          onRemove={() => handleRemove(group.label)}
          disableEdit={!facets}
          enabled={isEnabled(group.label)}
          onToggleEnabled={() => handleToggle(group.label)}
        />
      ))}
    </div>
  );
};

const APPLY_TO_LABELS: Record<ColorByTarget, string> = {
  map: 'Map',
  probitPlot: 'Probit Plot',
  monthlyVolumes: 'Monthly Volumes',
  ratios: 'Ratios',
  crossPlot: 'Cross Plot',
};

const AppliedColorBy = ({
  onEditRule,
}: {
  onEditRule: (ruleId: string) => void;
}) => {
  const [rules, setRules] = useAtom(tcColorByRulesAtom);
  const facets = useAtomValue(tcWellsFacetsAtom);

  if (rules.length === 0) return null;

  const handleRemove = (ruleId: string) => {
    setRules(rules.filter((r) => r.ruleId !== ruleId));
  };

  const handleDuplicate = (rule: ColorByConfig) => {
    const duplicated: ColorByConfig = {
      ...rule,
      ruleId: `${rule.attribute}-${Date.now()}-${Math.round(
        Math.random() * 1000
      )}`,
      valueColors: { ...rule.valueColors },
      applyTo: { ...rule.applyTo },
      enabled: true,
    };
    setRules([...rules, duplicated]);
  };

  const handleToggleEnabled = (ruleId: string) => {
    setRules(
      rules.map((r) => {
        return r.ruleId === ruleId ? { ...r, enabled: !r.enabled } : r;
      })
    );
  };

  const handleValueColorChange = (
    ruleId: string,
    value: string,
    newColor: string,
    newOpacity: number
  ) => {
    setRules(
      rules.map((r) => {
        return r.ruleId === ruleId
          ? {
              ...r,
              valueColors: {
                ...r.valueColors,
                [value]: hexToRgba(newColor, newOpacity),
              },
            }
          : r;
      })
    );
  };

  return (
    <>
      {rules.map((rule) => {
        const categoryLabel =
          TC_WELL_FILTER_CATEGORIES.find((c) => c.id === rule.attribute)
            ?.label ?? rule.attribute;

        const activeTargets = Object.entries(rule.applyTo)
          .filter(([, active]) => active)
          .map(([key]) => APPLY_TO_LABELS[key as ColorByTarget]);

        return (
          <div
            key={rule.ruleId}
            className={classNames(styles.appliedColorBy, {
              [styles.appliedColorByDisabled]: !rule.enabled,
            })}
          >
            <div className={styles.appliedFilterHeader}>
              <span className={styles.appliedFilterName}>{categoryLabel}</span>
              <div className={styles.appliedFilterActions}>
                <button
                  type="button"
                  className={classNames(
                    styles.polygonAction,
                    styles.polygonActionEdit
                  )}
                  onClick={() => onEditRule(rule.ruleId)}
                  aria-label="Edit rule"
                  disabled={!facets}
                >
                  <Edit />
                </button>
                <button
                  type="button"
                  className={classNames(
                    styles.polygonAction,
                    styles.polygonActionEdit
                  )}
                  onClick={() => handleDuplicate(rule)}
                  aria-label="Duplicate rule"
                >
                  <Copy />
                </button>
                <button
                  type="button"
                  className={styles.polygonAction}
                  onClick={() => handleRemove(rule.ruleId)}
                  aria-label="Remove rule"
                >
                  <Close />
                </button>
              </div>
            </div>
            <div className={styles.ruleControlsRow}>
              {activeTargets.length > 0 && (
                <div className={styles.colorByActiveOn}>
                  <span className={styles.colorByActiveLabel}>Active on:</span>
                  <div className={styles.appliedFilterChips}>
                    {activeTargets.map((target) => (
                      <span key={target} className={styles.appliedFilterChip}>
                        {target}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <Button
                kind="ghost"
                size="sm"
                onClick={() => handleToggleEnabled(rule.ruleId)}
              >
                {rule.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
            {rule.selectedValues.length > 0 && (
              <div className={styles.colorByValues}>
                {rule.selectedValues.map((value) => {
                  const cat = TC_WELL_FILTER_CATEGORIES.find(
                    (c) => c.id === rule.attribute
                  );
                  const displayName = cat
                    ? resolveFacetName(value, cat.param, facets)
                    : value;
                  return (
                    <div key={value} className={styles.colorByValueRow}>
                      <ColorSwatch
                        color={
                          rule.valueColors[value] ??
                          DEFAULT_COLORS[
                            rule.selectedValues.indexOf(value) %
                              DEFAULT_COLORS.length
                          ]
                        }
                        onChange={(newColor, newOpacity) =>
                          handleValueColorChange(
                            rule.ruleId,
                            value,
                            newColor,
                            newOpacity
                          )
                        }
                      />
                      <span>{displayName}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

const AppliedUnitColorBy = ({
  onEditRule,
}: {
  onEditRule: (ruleId: string) => void;
}) => {
  const [rules, setRules] = useAtom(tcUnitColorByRulesAtom);
  const facets = useAtomValue(tcUnitsFacetsAtom);

  if (rules.length === 0) return null;

  const handleRemove = (ruleId: string) => {
    setRules(rules.filter((r) => r.ruleId !== ruleId));
  };

  const handleDuplicate = (rule: ColorByConfig) => {
    const duplicated: ColorByConfig = {
      ...rule,
      ruleId: `${rule.attribute}-${Date.now()}-${Math.round(
        Math.random() * 1000
      )}`,
      valueColors: { ...rule.valueColors },
      applyTo: { ...rule.applyTo },
      enabled: true,
    };
    setRules([...rules, duplicated]);
  };

  const handleToggleEnabled = (ruleId: string) => {
    setRules(
      rules.map((r) => {
        return r.ruleId === ruleId ? { ...r, enabled: !r.enabled } : r;
      })
    );
  };

  const handleValueColorChange = (
    ruleId: string,
    value: string,
    newColor: string,
    newOpacity: number
  ) => {
    setRules(
      rules.map((r) => {
        return r.ruleId === ruleId
          ? {
              ...r,
              valueColors: {
                ...r.valueColors,
                [value]: hexToRgba(newColor, newOpacity),
              },
            }
          : r;
      })
    );
  };

  return (
    <>
      {rules.map((rule) => {
        const categoryLabel =
          TC_UNIT_FILTER_CATEGORIES.find((c) => c.id === rule.attribute)
            ?.label ?? rule.attribute;

        return (
          <div
            key={rule.ruleId}
            className={classNames(styles.appliedColorBy, {
              [styles.appliedColorByDisabled]: !rule.enabled,
            })}
          >
            <div className={styles.appliedFilterHeader}>
              <span className={styles.appliedFilterName}>{categoryLabel}</span>
              <div className={styles.appliedFilterActions}>
                <button
                  type="button"
                  className={classNames(
                    styles.polygonAction,
                    styles.polygonActionEdit
                  )}
                  onClick={() => onEditRule(rule.ruleId)}
                  aria-label="Edit rule"
                  disabled={!facets}
                >
                  <Edit />
                </button>
                <button
                  type="button"
                  className={classNames(
                    styles.polygonAction,
                    styles.polygonActionEdit
                  )}
                  onClick={() => handleDuplicate(rule)}
                  aria-label="Duplicate rule"
                >
                  <Copy />
                </button>
                <button
                  type="button"
                  className={styles.polygonAction}
                  onClick={() => handleRemove(rule.ruleId)}
                  aria-label="Remove rule"
                >
                  <Close />
                </button>
              </div>
            </div>
            <div className={styles.ruleControlsRow}>
              <Button
                kind="ghost"
                size="sm"
                onClick={() => handleToggleEnabled(rule.ruleId)}
              >
                {rule.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
            {rule.selectedValues.length > 0 && (
              <div className={styles.colorByValues}>
                {rule.selectedValues.map((value) => {
                  const cat = TC_UNIT_FILTER_CATEGORIES.find(
                    (c) => c.id === rule.attribute
                  );
                  const displayName = cat
                    ? resolveUnitFacetName(value, cat.param, facets)
                    : value;
                  return (
                    <div key={value} className={styles.colorByValueRow}>
                      <ColorSwatch
                        color={
                          rule.valueColors[value] ??
                          DEFAULT_COLORS[
                            rule.selectedValues.indexOf(value) %
                              DEFAULT_COLORS.length
                          ]
                        }
                        onChange={(newColor, newOpacity) =>
                          handleValueColorChange(
                            rule.ruleId,
                            value,
                            newColor,
                            newOpacity
                          )
                        }
                      />
                      <span>{displayName}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

interface TypeCurvePageFiltersProps {
  wellCount?: number;
  unitCount?: number;
  onEditColorRule: (ruleId: string | null) => void;
  onEditUnitColorRule: (ruleId: string | null) => void;
  dcaScenarioName?: string;
  onEditDcaScenario?: () => void;
  typeCurveAreaName?: string | null;
  /** Page Settings checkboxes to offer; the Inventory Workspace page passes
   *  INVENTORY_PAGE_SECTIONS to add its Inventory Details toggle. */
  sections?: readonly PageSection[];
}

export const TypeCurvePageFilters = ({
  wellCount,
  unitCount,
  onEditColorRule,
  onEditUnitColorRule,
  dcaScenarioName,
  onEditDcaScenario,
  typeCurveAreaName,
  sections = PAGE_SECTIONS,
}: TypeCurvePageFiltersProps) => {
  // Pages whose section list omits charts (the Inventory Workspace) drop the
  // chart accordions along with the checkbox.
  const chartsOffered = sections.some((section) => section.key === 'charts');
  const activeTcWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const setFilterModalOpen = useSetAtom(tcWellFilterModalOpenAtom);
  const setFilterModalCategory = useSetAtom(tcWellFilterModalCategoryAtom);
  const setColorByModalOpen = useSetAtom(tcColorByModalOpenAtom);
  const appliedFilters = useAtomValue(tcWellFiltersAtom);
  const facets = useAtomValue(tcWellsFacetsAtom);
  const hasFacets = facets !== null;

  // Units accordion state
  const setUnitFilterModalOpen = useSetAtom(tcUnitFilterModalOpenAtom);
  const setUnitFilterModalCategory = useSetAtom(tcUnitFilterModalCategoryAtom);
  const setUnitColorByModalOpen = useSetAtom(tcUnitColorByModalOpenAtom);
  const appliedUnitFilters = useAtomValue(tcUnitFiltersAtom);
  const unitFacets = useAtomValue(tcUnitsFacetsAtom);
  const hasUnitFacets = unitFacets !== null;

  // Empty state (header has no Type Curve Area): also disable the add-filter and
  // color-by controls for both wells and units.
  const hasNoArea = useAtomValue(tcHasTypeCurveAreaAtom) === false;

  const hasAppliedFilters = useMemo(() => {
    return Object.entries(appliedFilters).some(
      ([, value]) =>
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    );
  }, [appliedFilters]);

  const hasAppliedUnitFilters = useMemo(() => {
    return Object.entries(appliedUnitFilters).some(
      ([, value]) =>
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    );
  }, [appliedUnitFilters]);

  const handleEditColorRule = useCallback(
    (ruleId: string) => {
      onEditColorRule(ruleId);
      setColorByModalOpen(true);
    },
    [onEditColorRule, setColorByModalOpen]
  );

  const handleAddColorRule = useCallback(() => {
    onEditColorRule(null);
    setColorByModalOpen(true);
  }, [onEditColorRule, setColorByModalOpen]);

  const handleEditUnitColorRule = useCallback(
    (ruleId: string) => {
      onEditUnitColorRule(ruleId);
      setUnitColorByModalOpen(true);
    },
    [onEditUnitColorRule, setUnitColorByModalOpen]
  );

  const handleAddUnitColorRule = useCallback(() => {
    onEditUnitColorRule(null);
    setUnitColorByModalOpen(true);
  }, [onEditUnitColorRule, setUnitColorByModalOpen]);

  return (
    <div className={styles.filtersRoot}>
      <Accordion defaultExpanded>
        <AccordionSummary>
          <div className={styles.filterSectionTitle}>Type Curve Settings</div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.filterSection}>
            {activeTcWorkspace && !activeTcWorkspace.promoted ? (
              <TcAreaPickerButton
                typeCurveAreaName={typeCurveAreaName ?? null}
              />
            ) : null}
            {dcaScenarioName && onEditDcaScenario ? (
              <div className={styles.dcaScenario}>
                <span className={styles.dcaScenarioLabel}>DCA Scenario:</span>
                <span className={styles.dcaScenarioName}>
                  {dcaScenarioName}
                </span>
                <Button
                  hasIconOnly
                  kind="ghost"
                  size="sm"
                  className={styles.dcaScenarioEdit}
                  onClick={onEditDcaScenario}
                  iconDescription="Edit DCA Scenario"
                  tooltipPosition="top"
                  renderIcon={Edit}
                />
              </div>
            ) : null}
            <PageSettings sections={sections} />
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <AccordionSummary>
          <div className={styles.filterSectionTitle}>Global Map Filter</div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.filterSection}>
            <GlobalMapFilter />
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <AccordionSummary>
          <div className={styles.filterSectionTitle}>
            {`Wells${
              wellCount !== undefined ? ` (${format0(wellCount)})` : ''
            }`}
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.filterSection}>
            <div className={styles.wellsSection}>
              <div className={styles.wellsFilterActions}>
                <Button
                  kind="tertiary"
                  size="sm"
                  onClick={() => {
                    setFilterModalCategory(null);
                    setFilterModalOpen(true);
                  }}
                  renderIcon={Add}
                  disabled={!hasFacets || hasNoArea}
                >
                  Add Filter
                </Button>
              </div>

              {hasAppliedFilters ? (
                <AppliedFilters />
              ) : (
                <p className={styles.emptyFiltersText}>
                  No filters have been added yet
                </p>
              )}

              <div className={styles.wellsFilterActions}>
                <Button
                  kind="tertiary"
                  size="sm"
                  onClick={handleAddColorRule}
                  renderIcon={Add}
                  disabled={!hasFacets || hasNoArea}
                >
                  Color by Attribute
                </Button>
              </div>

              <AppliedColorBy onEditRule={handleEditColorRule} />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <AccordionSummary>
          <div className={styles.filterSectionTitle}>
            {`Units${
              unitCount !== undefined ? ` (${format0(unitCount)})` : ''
            }`}
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.filterSection}>
            <div className={styles.wellsSection}>
              <div className={styles.wellsFilterActions}>
                <Button
                  kind="tertiary"
                  size="sm"
                  onClick={() => {
                    setUnitFilterModalCategory(null);
                    setUnitFilterModalOpen(true);
                  }}
                  renderIcon={Add}
                  disabled={!hasUnitFacets || hasNoArea}
                >
                  Add Filter
                </Button>
              </div>

              {hasAppliedUnitFilters ? (
                <AppliedUnitFilters />
              ) : (
                <p className={styles.emptyFiltersText}>
                  No Filters have been added yet
                </p>
              )}

              <div className={styles.wellsFilterActions}>
                <Button
                  kind="tertiary"
                  size="sm"
                  onClick={handleAddUnitColorRule}
                  renderIcon={Add}
                  disabled={!hasUnitFacets || hasNoArea}
                >
                  Color by Attribute
                </Button>
              </div>

              <AppliedUnitColorBy onEditRule={handleEditUnitColorRule} />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      {chartsOffered && (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary>
              <div className={styles.filterSectionTitle}>Probit Plot Chart</div>
            </AccordionSummary>
            <AccordionDetails>
              <div className={styles.filterSection}>
                <ProbitPlotChart />
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary>
              <div className={styles.filterSectionTitle}>
                Monthly Volumes Chart
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <div className={styles.filterSection}>
                <MonthlyVolumesChart />
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary>
              <div className={styles.filterSectionTitle}>Ratios Chart</div>
            </AccordionSummary>
            <AccordionDetails>
              <div className={styles.filterSection}>
                <RatiosChart />
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary>
              <div className={styles.filterSectionTitle}>Cross Plot Chart</div>
            </AccordionSummary>
            <AccordionDetails>
              <div className={styles.filterSection}>
                <CrossPlotChart />
              </div>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </div>
  );
};
